import type { ReactNode } from 'react';
import { cx } from '../cx';
import { WIDE_ONLY, type ChartLayout } from './layout';
import { TICK_TEXT, VALUE_TEXT, niceScale, percentOf } from './scale';

export type WaterfallStep = {
  id: string;
  label: ReactNode;
  /** A total (the start and end) or a change from the running total. */
  kind: 'total' | 'change';
  /** For a total, its value; for a change, signed. */
  value: number;
  /** Formatted, signed for changes ("+1,204", "−987"). */
  display: ReactNode;
};

export type WaterfallProps = {
  steps: WaterfallStep[];
  formatTick: (value: number) => string;
  /**
   * Where the axis starts, when the changes are small next to the totals (say so in the subtitle:
   * "axis starts at 18,000"). Defaults to zero.
   */
  from?: number;
  /** `auto` (default) switches to the narrow form in a narrow container: a shorter plot, value labels only on the totals and the largest change, and truncated step labels. */
  layout?: ChartLayout;
  /** One sentence that walks from the start to the end, for screen readers. */
  description: string;
  className?: string;
};

const PLOT: Record<ChartLayout, string> = { auto: 'h-36 @md:h-48', narrow: 'h-36', wide: 'h-48' };
const STEP_LABEL: Record<ChartLayout, string> = {
  auto: 'min-w-0 truncate @md:min-w-auto @md:overflow-visible @md:text-clip @md:whitespace-normal',
  narrow: 'min-w-0 truncate',
  wide: '',
};

/** Running totals of a waterfall: [bottom, top] of each bar. Exported for tests. */
export function waterfallBars(steps: WaterfallStep[]): Array<[number, number]> {
  let running = 0;
  return steps.map((s) => {
    if (s.kind === 'total') {
      running = s.value;
      return [0, s.value];
    }
    const from = running;
    running += s.value;
    return [Math.min(from, running), Math.max(from, running)];
  });
}

/**
 * How a total got from one figure to the next: totals in ink from zero, increases in gold and
 * decreases in iris floating from the running total, joined by thin connectors. For "how the index
 * changed this week": open last week, + new, - closed, open now.
 */
export function Waterfall({ steps, formatTick, from = 0, layout = 'auto', description, className }: WaterfallProps) {
  const bars = waterfallBars(steps);
  const scale = from
    ? niceScale(from, Math.max(...bars.map((b) => b[1])))
    : niceScale(0, Math.max(...bars.map((b) => b[1])), { zero: true });
  const largest = steps.reduce(
    (best, s, i) => (s.kind === 'change' && (best < 0 || Math.abs(s.value) > Math.abs(steps[best].value)) ? i : best),
    -1,
  );
  const floor = Math.max(scale.min, from);
  const pos = (v: number) => percentOf(v, scale.min, scale.max);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-1', className)}>
      <div className="flex gap-2" aria-hidden="true">
        <div className={cx('relative w-12 shrink-0', PLOT[layout])}>
          {scale.ticks.map((t) => (
            <span
              key={t}
              className={cx('absolute right-0 translate-y-1/2', TICK_TEXT)}
              style={{ bottom: `${pos(t)}%` }}
            >
              {formatTick(t)}
            </span>
          ))}
        </div>
        <div className={cx('relative flex-1', PLOT[layout])}>
          {scale.ticks.map((t) => (
            <span
              key={t}
              className="absolute inset-x-0 border-t border-jc-chart-grid"
              style={{ bottom: `${pos(t)}%` }}
            />
          ))}
          <div className="absolute inset-0 flex gap-3">
            {steps.map((s, i) => {
              const [rawLo, hi] = bars[i];
              const lo = Math.max(rawLo, floor);
              const fill =
                s.kind === 'total' ? 'bg-jc-ink' : s.value >= 0 ? 'bg-jc-chart-div-pos-2' : 'bg-jc-chart-div-neg-2';
              const level = s.kind === 'total' ? hi : s.value >= 0 ? hi : lo;
              return (
                <div key={s.id} className="relative h-full flex-1">
                  <span
                    className={cx('absolute inset-x-0 min-h-0.5 rounded-jc-info', fill)}
                    style={{ bottom: `${pos(lo)}%`, height: `${pos(hi) - pos(lo)}%` }}
                  />
                  {i < steps.length - 1 ? (
                    <span
                      className="absolute -right-3 left-full border-t border-dashed border-jc-ink-secondary"
                      style={{ bottom: `${pos(level)}%` }}
                    />
                  ) : null}
                  <span
                    className={cx(
                      'absolute inset-x-0 pb-1 text-center',
                      VALUE_TEXT,
                      s.kind === 'change' && i !== largest && WIDE_ONLY[layout],
                    )}
                    style={{ bottom: `${pos(hi)}%` }}
                  >
                    {s.display}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex gap-3 pl-14" aria-hidden="true">
        {steps.map((s) => (
          <span key={s.id} className={cx('flex-1 text-center', STEP_LABEL[layout], TICK_TEXT)}>
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}
